import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Filter, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { selectCls } from "@/pages/Take5New";
import { CONTROL_LEVELS } from "@/lib/labels";

export const PAGE_SIZE = 25;

const HazardFilter = ({ options, value, onChange }) => {
  const [find, setFind] = useState("");
  const shown = options.filter((o) => o.label.toLowerCase().includes(find.toLowerCase()));
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" data-testid="measures-hazard-filter" className="h-11 justify-between gap-2 sm:w-72">
          <span className="flex items-center gap-2 truncate"><Filter className="h-4 w-4" />{value.length ? `${value.length} danger(s) sélectionné(s)` : "Tous les dangers"}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(92vw,380px)] p-2">
        <Input data-testid="measures-hazard-filter-search" value={find} onChange={(e) => setFind(e.target.value)} placeholder="Chercher un danger…" className="mb-2 h-10" />
        <div className="max-h-72 overflow-y-auto">
          {shown.map((o) => (
            <label key={o.value} className="flex min-h-[38px] cursor-pointer items-center gap-2 rounded px-2 text-sm hover:bg-white/5">
              <input type="checkbox" data-testid={`measures-hazard-option-${o.value}`} className="h-4 w-4 accent-amber-500" checked={value.includes(o.value)} onChange={() => toggle(o.value)} /> {o.label}
            </label>
          ))}
        </div>
        {value.length > 0 && <Button variant="ghost" size="sm" data-testid="measures-hazard-filter-clear" className="mt-1 w-full" onClick={() => onChange([])}>Effacer le filtre</Button>}
      </PopoverContent>
    </Popover>
  );
};

export const MeasureFilters = ({ hazards, onChange }) => {
  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);
  const [level, setLevel] = useState("");
  useEffect(() => {
    const t = setTimeout(() => onChange({ q: q.trim(), hazards: sel.join(","), levels: level }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, sel, level]);
  return (
    <div className="mb-4 flex flex-col gap-2 sm:flex-row">
      <div className="relative flex-1">
        <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
        <Input data-testid="measures-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une mesure…" className="h-11 pl-9" />
      </div>
      <HazardFilter options={hazards || []} value={sel} onChange={setSel} />
      <select data-testid="measures-level-filter" aria-label="Niveau de maîtrise" className={`${selectCls} h-11 sm:w-64`} value={level} onChange={(e) => setLevel(e.target.value)}>
        <option value="">Tous les niveaux</option>
        {CONTROL_LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
        <option value="none">À classer</option>
      </select>
    </div>
  );
};

export const Pager = ({ page, total, onPage }) => {
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  return (
    <div className="mt-4 flex items-center justify-between gap-3 text-sm" data-testid="measures-pager">
      <span data-testid="measures-total" className="text-muted-foreground">{total} mesure(s)</span>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" data-testid="measures-prev" disabled={page <= 1} onClick={() => onPage(page - 1)}><ChevronLeft className="h-4 w-4" /></Button>
        <span data-testid="measures-page">Page {page} / {pages}</span>
        <Button variant="outline" size="sm" data-testid="measures-next" disabled={page >= pages} onClick={() => onPage(page + 1)}><ChevronRight className="h-4 w-4" /></Button>
      </div>
    </div>
  );
};
